import {firestore} from '../config/firebase';
import firestoreModule from '@react-native-firebase/firestore';

/**
 * Calculate distance between two coordinates using Haversine formula
 * Returns distance in miles
 */
function calculateDistance(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number {
  const R = 3959; // Earth's radius in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export interface SearchableAccount {
  id: string;
  name: string;
  municipality: string;
  distance?: number; // Distance in miles (if GPS search)
  publicVisibility: boolean;
  verified: boolean;
  location?: {latitude: number; longitude: number};
}

export const civiliansApi = {
  /**
   * Search for public accounts by GPS location (within radius)
   * Returns accounts sorted by distance
   */
  async searchAccountsByLocation(
    latitude: number,
    longitude: number,
    radiusMiles: number = 50,
  ): Promise<SearchableAccount[]> {
    try {
      console.log('🔍 Searching accounts by location:', {latitude, longitude, radiusMiles});
      
      // Get all public accounts
      // Note: Firestore doesn't support geo queries natively, so we'll fetch all public accounts
      // and filter by distance in memory. For production, consider using GeoFirestore or similar.
      const snapshot = await firestore()
        .collection('accounts')
        .where('publicVisibility', '==', true)
        .get();

      const accounts: SearchableAccount[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.verified !== true) return; // Only show verified accounts

        const accountLocation = data.location;

        if (accountLocation && accountLocation.latitude && accountLocation.longitude) {
          const distance = calculateDistance(
            latitude,
            longitude,
            accountLocation.latitude,
            accountLocation.longitude,
          );

          if (distance <= radiusMiles) {
            accounts.push({
              id: doc.id,
              name: data.name || '',
              municipality: data.municipality || '',
              distance: Math.round(distance * 10) / 10,
              publicVisibility: data.publicVisibility !== false,
              verified: true,
              location: {
                latitude: accountLocation.latitude,
                longitude: accountLocation.longitude,
              },
            });
          }
        } else {
          accounts.push({
            id: doc.id,
            name: data.name || '',
            municipality: data.municipality || '',
            publicVisibility: data.publicVisibility !== false,
            verified: true,
            location: undefined,
          });
        }
      });

      // Sort by distance
      accounts.sort((a, b) => (a.distance || 0) - (b.distance || 0));

      console.log(`✅ Found ${accounts.length} accounts within ${radiusMiles} miles`);
      return accounts;
    } catch (error) {
      console.error('Error searching accounts by location:', error);
      throw error;
    }
  },

  /**
   * Search for public accounts by city/municipality name
   */
  async searchAccountsByCity(cityName: string): Promise<SearchableAccount[]> {
    try {
      console.log('🔍 Searching accounts by city:', cityName);
      
      const cityLower = cityName.toLowerCase().trim();
      
      // Get all public accounts and filter by municipality name
      const snapshot = await firestore()
        .collection('accounts')
        .where('publicVisibility', '==', true)
        .get();

      const accounts: SearchableAccount[] = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        if (data.verified !== true) return; // Only show verified accounts

        const municipality = (data.municipality || '').toLowerCase();
        const name = (data.name || '').toLowerCase();

        const matchesMunicipality = municipality.includes(cityLower) || municipality.startsWith(cityLower);
        const matchesName = name.includes(cityLower) || name.startsWith(cityLower);
        
        const municipalityWords = municipality.split(/\s+/);
        const nameWords = name.split(/\s+/);
        const matchesMunicipalityWords = municipalityWords.some(word => word.startsWith(cityLower) || cityLower.startsWith(word));
        const matchesNameWords = nameWords.some(word => word.startsWith(cityLower) || cityLower.startsWith(word));

        if (matchesMunicipality || matchesName || matchesMunicipalityWords || matchesNameWords) {
          accounts.push({
            id: doc.id,
            name: data.name || '',
            municipality: data.municipality || '',
            publicVisibility: data.publicVisibility !== false,
            verified: true,
            location: data.location
              ? {
                  latitude: data.location.latitude,
                  longitude: data.location.longitude,
                }
              : undefined,
          });
        }
      });

      console.log(`✅ Found ${accounts.length} accounts matching "${cityName}"`);
      return accounts;
    } catch (error) {
      console.error('Error searching accounts by city:', error);
      throw error;
    }
  },

  /**
   * Check if civilian can follow more accounts
   * Free tier: 2 municipalities max
   * Subscribed: Unlimited
   */
  async canFollowMore(userId: string): Promise<{canFollow: boolean; reason?: string; currentCount: number}> {
    try {
      const userDoc = await firestore().collection('users').doc(userId).get();
      const userData = userDoc.data();
      
      const followedAccounts = userData?.followedAccounts || [];
      const currentCount = followedAccounts.length;
      const subscription = userData?.subscription;
      const hasActiveSubscription = subscription?.status === 'active';
      
      const FREE_MUNICIPALITY_LIMIT = 2;
      
      if (hasActiveSubscription) {
        return {canFollow: true, currentCount};
      }
      
      if (currentCount >= FREE_MUNICIPALITY_LIMIT) {
        return {
          canFollow: false,
          reason: `Free accounts can follow up to ${FREE_MUNICIPALITY_LIMIT} municipalities. Subscribe to follow unlimited municipalities.`,
          currentCount,
        };
      }
      
      return {canFollow: true, currentCount};
    } catch (error) {
      console.error('Error checking follow limit:', error);
      return {canFollow: true, currentCount: 0}; // Default to allowing on error
    }
  },

  /**
   * Follow an account (add to civilian's followedAccounts)
   */
  async followAccount(userId: string, accountId: string): Promise<void> {
    try {
      console.log('➕ Following account:', {userId, accountId});
      
      // Check if user can follow more
      const {canFollow, reason} = await this.canFollowMore(userId);
      if (!canFollow) {
        throw new Error(reason || 'Cannot follow more accounts');
      }
      
      await firestore()
        .collection('users')
        .doc(userId)
        .update({
          followedAccounts: firestoreModule.FieldValue.arrayUnion(accountId),
          [`notificationPreferences.${accountId}`]: true, // Default to notifications ON
          updatedAt: firestoreModule.FieldValue.serverTimestamp(),
        });

      console.log('✅ Account followed successfully');
    } catch (error) {
      console.error('Error following account:', error);
      throw error;
    }
  },

  /**
   * Unfollow an account (remove from civilian's followedAccounts)
   */
  async unfollowAccount(userId: string, accountId: string): Promise<void> {
    try {
      console.log('➖ Unfollowing account:', {userId, accountId});
      
      // Get current user data
      const userDoc = await firestore().collection('users').doc(userId).get();
      const userData = userDoc.data();
      const prefs = userData?.notificationPreferences || {};
      
      // Remove notification preference for this account
      const updatedPrefs = {...prefs};
      delete updatedPrefs[accountId];
      
      // Update both followedAccounts and notificationPreferences in a single atomic operation
      await firestore()
        .collection('users')
        .doc(userId)
        .update({
          followedAccounts: firestoreModule.FieldValue.arrayRemove(accountId),
          notificationPreferences: updatedPrefs,
          updatedAt: firestoreModule.FieldValue.serverTimestamp(),
        });

      console.log('✅ Account unfollowed successfully');
    } catch (error) {
      console.error('Error unfollowing account:', error);
      throw error;
    }
  },

  /**
   * Toggle notification preference for a followed account
   */
  async toggleNotificationPreference(
    userId: string,
    accountId: string,
    enabled: boolean,
  ): Promise<void> {
    try {
      console.log('🔔 Toggling notification preference:', {userId, accountId, enabled, enabledType: typeof enabled});
      
      // Ensure we're saving a boolean, not a string or other type
      const preferenceValue = enabled === true ? true : false;
      console.log('🔔 Saving preference value:', {preferenceValue, type: typeof preferenceValue});
      
      await firestore()
        .collection('users')
        .doc(userId)
        .update({
          [`notificationPreferences.${accountId}`]: preferenceValue,
          updatedAt: firestoreModule.FieldValue.serverTimestamp(),
        });

      console.log('✅ Notification preference updated in Firestore');
      
      // Verify it was saved correctly
      const userDoc = await firestore().collection('users').doc(userId).get();
      const savedPreferences = userDoc.data()?.notificationPreferences || {};
      const savedValue = savedPreferences[accountId];
      console.log('✅ Verified saved preference:', {
        accountId,
        savedValue,
        savedType: typeof savedValue,
        matches: savedValue === preferenceValue,
      });
    } catch (error) {
      console.error('Error toggling notification preference:', error);
      throw error;
    }
  },

  /**
   * Get details of a followed account
   */
  async getFollowedAccount(accountId: string): Promise<{
    id: string;
    name: string;
    displayName?: string;
    municipality: string;
    email?: string;
    civilianMapAccess?: boolean;
    verified?: boolean;
  } | null> {
    try {
      const accountDoc = await firestore().collection('accounts').doc(accountId).get();
      if (accountDoc.exists) {
        const data = accountDoc.data();
        
        // Try to get email from the first admin user
        let email: string | undefined;
        const adminIds = data?.adminIds || [];
        if (adminIds.length > 0) {
          try {
            const adminDoc = await firestore().collection('users').doc(adminIds[0]).get();
            email = adminDoc.data()?.email;
          } catch (e) {
            // If admin not found, try first user
            const userIds = data?.userIds || [];
            if (userIds.length > 0) {
              try {
                const userDoc = await firestore().collection('users').doc(userIds[0]).get();
                email = userDoc.data()?.email;
              } catch (e2) {
                // Email not available
              }
            }
          }
        } else {
          // No admins, try first user
          const userIds = data?.userIds || [];
          if (userIds.length > 0) {
            try {
              const userDoc = await firestore().collection('users').doc(userIds[0]).get();
              email = userDoc.data()?.email;
            } catch (e) {
              // Email not available
            }
          }
        }
        
        return {
          id: accountDoc.id,
          name: data?.name || 'Unknown',
          displayName: data?.displayName,
          municipality: data?.municipality || 'Unknown',
          email: email,
          civilianMapAccess: data?.civilianMapAccess,
          verified: data?.verified === true,
        };
      }
      return null;
    } catch (error) {
      console.error('Error getting followed account:', error);
      throw error;
    }
  },

  /**
   * Get civilian trial status
   * Returns trial info including days remaining and whether trial has expired
   */
  async getTrialStatus(userId: string): Promise<{
    isTrialActive: boolean;
    daysRemaining: number;
    trialEndDate: Date | null;
    hasSubscription: boolean;
  }> {
    try {
      const userDoc = await firestore().collection('users').doc(userId).get();
      const userData = userDoc.data();
      
      const subscription = userData?.subscription;
      const hasSubscription = subscription?.status === 'active';
      
      // If user has active subscription, trial doesn't matter
      if (hasSubscription) {
        return {
          isTrialActive: false,
          daysRemaining: 0,
          trialEndDate: null,
          hasSubscription: true,
        };
      }
      
      const trialStartDate = userData?.trialStartDate?.toDate();
      const TRIAL_DAYS = 30;
      
      if (!trialStartDate) {
        // No trial start date - assume trial started when account was created
        const createdAt = userData?.createdAt?.toDate() || new Date();
        const trialEndDate = new Date(createdAt.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
        const now = new Date();
        const daysRemaining = Math.max(0, Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
        
        return {
          isTrialActive: daysRemaining > 0,
          daysRemaining,
          trialEndDate,
          hasSubscription: false,
        };
      }
      
      const trialEndDate = new Date(trialStartDate.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
      const now = new Date();
      const daysRemaining = Math.max(0, Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
      
      return {
        isTrialActive: daysRemaining > 0,
        daysRemaining,
        trialEndDate,
        hasSubscription: false,
      };
    } catch (error) {
      console.error('Error getting trial status:', error);
      // Default to trial active on error
      return {
        isTrialActive: true,
        daysRemaining: 30,
        trialEndDate: null,
        hasSubscription: false,
      };
    }
  },

  /**
   * Check if civilian can access the app (has active trial or subscription)
   */
  async canAccessApp(userId: string): Promise<{canAccess: boolean; reason?: string}> {
    const trialStatus = await this.getTrialStatus(userId);
    
    if (trialStatus.hasSubscription) {
      return {canAccess: true};
    }
    
    if (trialStatus.isTrialActive) {
      return {canAccess: true};
    }
    
    return {
      canAccess: false,
      reason: 'Your 30-day free trial has expired. Subscribe to continue using ResponderMap.',
    };
  },
};

